
<template>
  <div class="number-range-input">
    <SvgIcon icon="solar:dollar-linear" />

    <el-input
      v-model="start"
      type="text"
      :placeholder="`${props.placeholder}(起)`"
      :formatter="numberFormatter"
      @keydown="(e) => onEnter(e, true)"
      @change="(val) => changeHandler(val, true, 'change')"
    />

    <span>
      -
    </span>

    <el-input
      v-model="end"
      type="text"
      :placeholder="`${props.placeholder}(止)`"
      :formatter="numberFormatter"
      @keydown="(e) => onEnter(e, false)"
      @change="(val) => changeHandler(val, false, 'change')"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, type PropType } from 'vue';
import SvgIcon from '../SvgIcon.vue';
import { isNull } from '@core/tools';
import { ElMessage } from 'element-plus';

let lock = false;

interface EmitValues {
  values: [string | undefined, string | undefined];
  error?: boolean;
}

defineOptions({ name: 'NumberRangeInput' });

defineExpose({
  clear: () => {
    start.value = '';
    end.value = '';
    emit('change', { values: [undefined, undefined] });
  },
});

const emit = defineEmits<{
  (e: 'enter', values: EmitValues): void;
  (e: 'change', values: EmitValues): void;
}>();

const props = defineProps({
  values: {
    type: Array as PropType<string[]>,
    required: false,
    default() {
      return [];
    },
  },
  placeholder: {
    type: String,
    required: true,
  },
});

const start = ref<string | undefined>(props.values[0] || undefined);
const end = ref<string | undefined>(props.values[1] || undefined);

const changeHandler = (val: string, isStart = false, emitName: 'change' | 'enter') => {
  if (val.charAt(val.length - 1) === '.') {
    if (isStart) start.value = String(parseFloat(val));
    if (!isStart) end.value = String(parseFloat(val));
  }

  if (end.value && start.value) {
    if (Number(start.value) > Number(end.value)) {
      if (!lock) ElMessage.error(`${props.placeholder}(起)不能大于${props.placeholder}(止)`);
      emitChange(val, isStart, emitName);
    } else {
      emitChange(val, isStart, emitName);
    }
  } else {
    emitChange(val, isStart, emitName);
  }

  lock = true;
  setTimeout(() => {
    lock = false;
  }, 100);
};

const emitChange = (val: string, isStart: boolean, emitName: 'change' | 'enter') => {
  if (isStart) {
    const isError = val && end.value ? Number(val) > Number(end.value) : false;

    // @ts-ignore
    emit(emitName, {
      values: [val, end.value],
      error: isError,
    });
  } else {
    const isError = val && start.value ? Number(start.value) > Number(val) : false;

    // @ts-ignore
    emit(emitName, {
      values: [start.value, val],
      error: isError,
    });
  }
};

const numberFormatter = (value: string) => {
  if (isNull(value)) return '';

  const xiaoshu = value.split('.')[1];

  if (value.includes('.') && isNull(xiaoshu)) return value;

  if (isNaN(parseFloat(value))) return '';

  if (xiaoshu && xiaoshu.charAt(xiaoshu.length - 1) === '0') return value;

  return parseFloat(value);
};

function onEnter(e: any, isStart = false) {
  if (e.keyCode !== 13) return;

  changeHandler(e.target.value, isStart, 'enter');
}
</script>

<style lang="scss" scoped>
.number-range-input {
  position: relative;
  cursor: text;
  display: inline-flex;
  justify-content: center;
  padding: 1px 10px 1px 28px;
  box-sizing: border-box;
  width: 100%;
  border-radius: 2px;
  height: 32px;
  align-items: center;
  font-size: 14px;
  box-shadow: 0 0 0 1px #d5d9e1 inset;

  & > span {
    display: inline-block;
    width: 20px;
    text-align: center;
  }

  & > .svg-icon {
    position: absolute;
    top: 8px;
    left: 8px;
    width: 16px;
    height: 16px;
    color: #999;
  }

  :deep(.el-input) {
    width: calc((100% - 20px) / 2);
    height: calc(100% - 2px);
  }

  :deep(.el-input__wrapper) {
    border: none !important;
    box-shadow: none !important;
    padding: 0 !important;
    height: 100% !important;
  }

  :deep(.el-input__inner) {
    height: 100% !important;
  }

  &:hover {
    box-shadow: 0 0 0 1px #c0c4cc inset;
  }

  &:focus-within {
    box-shadow: 0 0 0 1px var(--color-primary) inset;

    &:hover {
      box-shadow: 0 0 0 1px var(--color-primary) inset;
    }

    .svg-icon {
      color: var(--color-primary) !important;
    }
  }
}
</style>
